import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase";
import { useNavigate } from "react-router-dom";
import "../styles/register.css";
import { friendlyAuthError, friendlyDataError } from "../lib/friendlyErrors";

function EmailConfirmation() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Checking your email verification...");
  const [messageType, setMessageType] = useState("info");

  const getSavedProfile = useCallback((user) => {
    const savedProfile = localStorage.getItem("tempProfile");

    if (savedProfile) {
      try {
        return JSON.parse(savedProfile);
      } catch (error) {
        localStorage.removeItem("tempProfile");
      }
    }

    return user?.user_metadata || null;
  }, []);

  const saveProfile = useCallback(async (user) => {
    const profile = getSavedProfile(user);

    if (!profile) {
      return;
    }

    const { data: existingProfile, error: selectError } = await supabase
      .from("profiles")
      .select("id")
      .eq("id", user.id)
      .maybeSingle();

    if (selectError) {
      throw selectError;
    }

    if (existingProfile) {
      localStorage.removeItem("tempProfile");
      return;
    }

    const { error } = await supabase.rpc("save_profile_for_current_user", {
      p_first_name: profile.firstName,
      p_middle_name: profile.middleName,
      p_last_name: profile.lastName,
      p_phone: profile.phoneNumber,
      p_job_type: profile.jobType,
      p_organization_type: profile.organizationType,
      p_organization_name: profile.organizationName,
      p_country: profile.countryName,
      p_state: profile.stateName,
      p_city: profile.city,
    });

    if (error) {
      throw error;
    }

    localStorage.removeItem("tempProfile");
  }, [getSavedProfile]);

  const finishConfirmation = useCallback(async (session) => {
    const user = session?.user;

    if (!user?.email_confirmed_at) {
      setMessageType("info");
      setMessage("Your email has not been verified yet. Please open the latest verification email.");
      return;
    }

    try {
      await saveProfile(user);
      setMessageType("success");
      setMessage("Email verified successfully. Redirecting to home...");

      setTimeout(() => {
        navigate("/", { replace: true });
      }, 1500);
    } catch (error) {
      console.error("Profile save failed after email verification:", error);
      setMessageType("error");
      setMessage(friendlyDataError(error, "Your email was verified, but we could not save your profile details."));
    }
  }, [navigate, saveProfile]);

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session) {
        if (error) console.error("Email verification session check failed:", error);
        setMessageType("error");
        setMessage(friendlyAuthError(error, "The verification link is invalid or expired. Please request a new verification email."));
        return;
      }

      await finishConfirmation(session);
    };

    checkSession();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        finishConfirmation(session);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, [finishConfirmation]);

  return (
    <section className="auth-section">
      <div className="auth-box">
        <div className="welcome-message">
          <h2>Email verification</h2>
          <p>We are confirming your account and preparing your profile.</p>
        </div>

        <div className={`login-message ${messageType}`}>{message}</div>
      </div>
    </section>
  );
}

export default EmailConfirmation;
